// FinancialDashboard.jsx - обновленная версия
import React, {useEffect, useState} from 'react';
import {Alert, Card, Col, DatePicker, message, Row, Select, Spin, Table, Tag} from 'antd';
import dayjs from 'dayjs';
import {instance} from "../../../API/API";
import style from '../styles/expenses.dashboard.module.css';
import TotalExpensesCard from "./subcomponents/expenses_dashboard/TotalExpensesCard";
import SalaryCard from "./subcomponents/expenses_dashboard/SalaryCard";
import PeriodicExpensesCard from "./subcomponents/expenses_dashboard/PeriodicExpensesCard";
import OneTimeExpensesCard from "./subcomponents/expenses_dashboard/OneTimeExpensesCard";
import ExpensesByMonthsGraph from "./subcomponents/expenses_dashboard/ExpensesByMonthsGraph";
import ExpensesBuCategoriesGraph from "./subcomponents/expenses_dashboard/ExpensesBuCategoriesGraph";
import {CalendarOutlined, DollarOutlined, TeamOutlined} from '@ant-design/icons';

const {RangePicker} = DatePicker;
const {Option} = Select;


const DetailsByMonth = ({dashboardData, formatCurrency}) => {
    const columns = [
        {
            title: 'Месяц',
            dataIndex: 'month',
            key: 'month',
            width: 120,
            render: (text) => (
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <CalendarOutlined style={{color: '#1890ff'}}/>
                    <span style={{fontWeight: 500}}>{text}</span>
                </div>
            ),
        },
        {
            title: 'Общая сумма',
            dataIndex: 'total',
            key: 'total',
            width: 150,
            align: 'right',
            render: (value) => (
                <div style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: value > 0 ? '#cf1322' : '#8c8c8c'
                }}>
                    {formatCurrency(value)}
                </div>
            ),
            sorter: (a, b) => a.total - b.total,
        },
        {
            title: () => (
                <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <TeamOutlined style={{color: '#fa8c16'}}/>
                    <span>Зарплата</span>
                </div>
            ),
            dataIndex: 'salary',
            key: 'salary',
            width: 130,
            align: 'right',
            render: (value) => (
                <Tag
                    color={value > 0 ? 'orange' : 'default'}
                    style={{margin: 0, fontWeight: 500}}
                >
                    {formatCurrency(value)}
                </Tag>
            ),
            sorter: (a, b) => a.salary - b.salary,
        },
        {
            title: 'Периодические',
            dataIndex: 'periodic_expenses',
            key: 'periodic_expenses',
            width: 140,
            align: 'right',
            render: (value) => (
                <div style={{
                    color: '#389e0d',
                    fontWeight: value > 0 ? 500 : 'normal'
                }}>
                    {formatCurrency(value)}
                </div>
            ),
            sorter: (a, b) => a.periodic_expenses - b.periodic_expenses,
        },
        {
            title: 'Разовые',
            dataIndex: 'one_time_expenses',
            key: 'one_time_expenses',
            width: 130,
            align: 'right',
            render: (value) => (
                <div style={{
                    color: '#722ed1',
                    fontWeight: value > 0 ? 500 : 'normal'
                }}>
                    {formatCurrency(value)}
                </div>
            ),
            sorter: (a, b) => a.one_time_expenses - b.one_time_expenses,
        },
        {
            title: 'Сотрудников',
            dataIndex: 'employee_count',
            key: 'employee_count',
            width: 120,
            align: 'center',
            render: (value) => (
                value > 0 ? (
                    <Tag
                        icon={<TeamOutlined/>}
                        color="blue"
                        style={{
                            margin: 0,
                            minWidth: 60,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 4
                        }}
                    >
                        {value}
                    </Tag>
                ) : (
                    <span style={{color: '#bfbfbf'}}>-</span>
                )
            ),
            sorter: (a, b) => a.employee_count - b.employee_count,
        },
        {
            title: 'Оплата',
            key: 'payment',
            width: 160,
            render: (_, record) => (
                <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                    <div style={{fontSize: '12px', color: '#52c41a'}}>
                        Наличные: {formatCurrency(record.cash)}
                    </div>
                    <div style={{fontSize: '12px', color: '#1890ff'}}>
                        Безнал: {formatCurrency(record.non_cash)}
                    </div>
                </div>
            ),
        },
    ];

    const dataSource = dashboardData.monthly_expenses.map((item, index) => ({
        key: index,
        ...item,
        month: item.month.replace('2025-', ''),
    }));

    // Суммарная строка
    const summaryRow = {
        key: 'summary',
        month: 'Итого',
        total: dashboardData.stats.total_expenses,
        salary: dashboardData.stats.total_salary,
        periodic_expenses: dashboardData.stats.total_periodic,
        one_time_expenses: dashboardData.stats.total_one_time,
        employee_count: dashboardData.stats.employee_count,
        cash: dashboardData.stats.total_cash,
        non_cash: dashboardData.stats.total_non_cash,
    };

    return (
        <Card
            title={
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <DollarOutlined style={{color: '#52c41a'}}/>
                    <span>Детализация расходов по месяцам</span>
                    <Tag
                        color="gold"
                        style={{marginLeft: 'auto', fontWeight: 500}}
                    >
                        {formatCurrency(dashboardData.stats.total_expenses)}
                    </Tag>
                </div>
            }
            style={{borderRadius: 8}}
            bodyStyle={{padding: 0}}
        >
            <Table
                columns={columns}
                dataSource={[...dataSource, summaryRow]}
                pagination={false}
                size="middle"
                scroll={{y: 300}}
                rowClassName={(record) =>
                    record.key === 'summary' ? 'ant-table-row-summary' : ''
                }
                onRow={(record) => {
                    if (record.key === 'summary') {
                        return {
                            style: {
                                backgroundColor: '#f6ffed',
                                fontWeight: 'bold',
                            }
                        };
                    }
                    return {};
                }}
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={7}>
                                <div style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#fafafa',
                                    borderTop: '1px solid #f0f0f0'
                                }}>
                                    <div style={{display: 'flex', gap: 16}}>
                                        <div>
                                            <span style={{color: '#8c8c8c', marginRight: 8}}>Среднемесячно:</span>
                                            <Tag color="purple">{formatCurrency(dashboardData.stats.avg_monthly)}</Tag>
                                        </div>
                                        <div>
                                            <span style={{color: '#8c8c8c', marginRight: 8}}>Зарплата:</span>
                                            <Tag
                                                color="orange">{formatCurrency(dashboardData.stats.salary_monthly_avg)}</Tag>
                                        </div>
                                        <div>
                                            <span style={{color: '#8c8c8c', marginRight: 8}}>Периодические:</span>
                                            <Tag
                                                color="green">{formatCurrency(dashboardData.stats.periodic_monthly_avg)}</Tag>
                                        </div>
                                        <div>
                                            <span style={{color: '#8c8c8c', marginRight: 8}}>Разовые:</span>
                                            <Tag
                                                color="magenta">{formatCurrency(dashboardData.stats.one_time_monthly_avg)}</Tag>
                                        </div>
                                    </div>
                                </div>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />

            <style jsx global>{`
                .ant-table-row-summary td {
                    border-bottom: 2px solid #1890ff !important;
                }

                .ant-table-thead > tr > th {
                    background-color: #fafafa;
                    font-weight: 600;
                }

                .ant-table-tbody > tr:hover > td {
                    background-color: #f0f9ff !important;
                }
            `}</style>
        </Card>
    );
};


const FinancialDashboard = () => {
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('year'),
        dayjs().endOf('year'),
    ]);
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        monthly_expenses: [],
        by_category: [],
        stats: {
            total_expenses: 0,
            total_salary: 0,
            total_one_time: 0,
            total_periodic: 0,
            avg_monthly: 0,
            employee_count: 0,
            periodic_monthly_avg: 0,
            one_time_monthly_avg: 0,
            salary_monthly_avg: 0
        },
        periodic_total: 0,
        one_time_total: 0,
        salary_total: 0,
    });
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                year: selectedYear,
                date_from: dateRange[0].format('YYYY-MM-DD'),
                date_to: dayjs(dateRange[1].format('YYYY-MM-DD'),'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD'),
            };

            if (selectedCategory) {
                params.category_id = selectedCategory;
            }

            const response = await instance.get(`finance_04122025/get_expense_analytics.php`, {
                params,
                withCredentials: true,
            });

            if (response.data.success) {
                setDashboardData(response.data.data);
            } else {
                throw new Error(response.data.error || 'Failed to load data');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message);
            message.error('Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await instance.get(`finance_04122025/expense_categories.php`, {
                withCredentials: true,
            });
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        } finally {
            setLoadingCategories(false);
        }
    };


    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchData();
    }, [selectedYear, dateRange, selectedCategory]);

    const handleDateChange = (dates) => {
        if (dates) {
            setDateRange(dates);
        }
    };

    const handleYearChange = (value) => {
        setSelectedYear(value);
    };

    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    if (error) {
        return (
            <div style={{padding: '20px'}}>
                <Alert
                    message="Ошибка"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{padding: '20px'}}>
            {/* Фильтры */}
            <Card style={{marginBottom: 20}}>
                <div className={style.container__filters__field}>
                    <div style={{display: 'flex', alignItems: 'center', marginTop: 14}}>
                        <span style={{marginRight: 10, marginTop: '-15px'}}>Период:</span>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateChange}
                            picker="month"
                            size="small"
                            format="YYYY-MM"
                        />
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <span style={{marginRight: 10}}>Год:</span>
                        <Select
                            value={selectedYear}
                            onChange={handleYearChange}
                            style={{width: 100}}
                            size="small"
                            loading={loading}
                        >
                            {[2023, 2024, 2025, 2026].map(year => (
                                <Option key={year} value={year}>{year}</Option>
                            ))}
                        </Select>
                    </div>
                    {/*                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <Select
                            placeholder="Все категории"
                            style={{width: '250px'}}
                            allowClear
                            size="small"
                            onChange={handleCategoryChange}
                            loading={loading}
                        >
                            <Option value={null}>Все категории</Option>
                            {categories.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </div>*/}
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <span style={{color: '#999', fontSize: '12px'}}>
                            Данные обновлены: {dayjs().format('DD.MM.YYYY HH:mm')}
                        </span>
                    </div>
                </div>
            </Card>

            {loading ? (
                <div style={{textAlign: 'center', padding: '50px'}}>
                    <Spin size="large" tip="Загрузка данных..."/>
                </div>
            ) : (
                <>
                    {/* Основная статистика: Общие расходы, Зарплата, Периодические, Разовые */}
                    <Row gutter={[16, 16]} className={style.dashboard__stats}>
                        <Col xs={24} sm={12} md={12} lg={6}>
                            <TotalExpensesCard
                                dateRange={dateRange}
                                selectedCategory={selectedCategory}
                                refreshKey={refreshKey}
                            />
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6}>
                            <SalaryCard
                                dateRange={dateRange}
                                selectedCategory={selectedCategory}
                                refreshKey={refreshKey}
                            />
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6}>
                            <PeriodicExpensesCard
                                dateRange={dateRange}
                                selectedCategory={selectedCategory}
                                refreshKey={refreshKey}
                            />
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={6}>
                            <OneTimeExpensesCard
                                dateRange={dateRange}
                                selectedCategory={selectedCategory}
                                refreshKey={refreshKey}
                            />
                        </Col>
                    </Row>

                    {/* Графики */}
                    <ExpensesByMonthsGraph dashboardData={dashboardData} formatCurrency={formatCurrency}/>
                    <ExpensesBuCategoriesGraph dashboardData={dashboardData} formatCurrency={formatCurrency}/>
                    <DetailsByMonth dashboardData={dashboardData} formatCurrency={formatCurrency}/>
                </>
            )}
        </div>
    );
};

export default FinancialDashboard;